import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../i18n/LocalizationProvider';
import type { TranslationContent } from '../i18n/translations';

interface CouponHistory {
  code: string;
  value: number;
  redeemedAt: string;
}

export default function CouponsPage() {
  const [history, setHistory] = useState<CouponHistory[]>([]);
  const [redeemCode, setRedeemCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { t } = useTranslation();
  const statsLabels = t<TranslationContent['coupons']['stats']>('coupons.stats');
  const pricing = t<TranslationContent['coupons']['pricing']>('coupons.pricing');
  const historyHeaders = t<TranslationContent['coupons']['history']['table']>('coupons.history.table');

  useEffect(() => {
    let mounted = true;
    setTimeout(() => {
      if (!mounted) return;
      try {
        setHistory([
          { code: 'ABC123', value: 100, redeemedAt: '2025-07-15' },
          { code: 'XYZ789', value: 500, redeemedAt: '2025-07-20' },
        ]);
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      mounted = false;
    };
  }, []);

  const totals = useMemo(() => {
    const taken = history.length;
    const available = history.reduce((sum, item) => sum + item.value, 0);
    return { taken, available };
  }, [history]);

  const handleRedeem = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!redeemCode.trim()) return;

    try {
      const response = await fetch(`/api/redeem?code=${encodeURIComponent(redeemCode.trim())}`);
      if (!response.ok) {
        throw new Error(`Redeem failed with status ${response.status}`);
      }
      alert(t('coupons.redeem.success', { code: redeemCode }));
      setRedeemCode('');
    } catch (err) {
      console.error(err);
      alert(t('coupons.redeem.failure'));
    }
  };

  if (loading) {
    return (
      <div className="panel" style={{ textAlign: 'center' }}>
        <p className="text-muted">{t('coupons.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel">
        <p className="text-muted" style={{ color: '#c13a3a' }}>{t('coupons.error')}</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="section-heading">
        <div>
          <span className="badge">{t('coupons.badge')}</span>
          <h2>{t('coupons.title')}</h2>
          <p className="text-muted">{t('coupons.description')}</p>
        </div>
        <div className="metric-card" style={{ margin: 0, padding: '1rem 1.25rem' }}>
          <span>{t('coupons.totalValue')}</span>
          <strong>${totals.available}</strong>
        </div>
      </div>

      <form
        onSubmit={handleRedeem}
        className="panel"
        style={{ boxShadow: 'none', border: '1px dashed var(--color-border)', marginBottom: '2rem' }}
      >
        <h3>{t('coupons.redeem.title')}</h3>
        <p className="text-muted">{t('coupons.redeem.description')}</p>
        <div className="form-grid two-cols">
          <input
            placeholder={t('coupons.redeem.placeholder')}
            value={redeemCode}
            onChange={event => setRedeemCode(event.target.value)}
          />
          <button type="submit">{t('coupons.redeem.submit')}</button>
        </div>
      </form>

      <div className="metric-grid" style={{ marginBottom: '2rem' }}>
        <div className="metric-card">
          <span>{statsLabels.redeemed}</span>
          <strong>{totals.taken}</strong>
        </div>
        <div className="metric-card">
          <span>{statsLabels.testsUnlocked}</span>
          <strong>{history.reduce((sum, item) => sum + Math.round(item.value / 10), 0)}</strong>
        </div>
        <div className="metric-card">
          <span>{statsLabels.inventoryHealth}</span>
          <strong>{t('coupons.stats.inventoryLabel', { count: Math.max(0, 120 - totals.taken) })}</strong>
        </div>
      </div>

      <div className="panel" style={{ boxShadow: 'none', marginBottom: '2rem' }}>
        <h3>{pricing.title}</h3>
        <p className="text-muted">{pricing.description}</p>
        <div className="metric-grid">
          {pricing.tiers.map(tier => (
            <div key={tier.name} className="metric-card" style={{ alignSelf: 'stretch' }}>
              <span>{tier.name}</span>
              <strong>{tier.price}</strong>
              <p className="text-muted">{tier.features}</p>
              <p className="text-muted">{tier.includes}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="panel" style={{ boxShadow: 'none' }}>
        <h3>{t('coupons.history.title')}</h3>
        <table>
          <thead>
            <tr>
              <th>{historyHeaders.code}</th>
              <th>{historyHeaders.value}</th>
              <th>{historyHeaders.redeemed}</th>
            </tr>
          </thead>
          <tbody>
            {history.map(item => (
              <tr key={item.code}>
                <td>{item.code}</td>
                <td>${item.value}</td>
                <td>{item.redeemedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
